import {
  isLoaded,
  isReserved,
  PolicyNumber,
  PolicyNumberError,
  PolicyNumberValidating,
  Reserved,
  UserAssignedPolicyNumber,
} from 'ApplicationEntry/models/PolicyNumber';
import {EmptyPolicyTypes, isLoadedPolicyTypes, PolicyTypes} from 'ApplicationEntry/models/PolicyTypes';
import {Async, Loaded} from 'Core/models/Async';
import { EmptyString, NonEmptyString, StringUnion } from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export class InvalidNewYorkCaseDetails {
  public readonly key = 'InvalidNewYorkCaseDetails';

  constructor(public readonly policyNumber: PolicyNumber, public readonly productType: PolicyTypes, public readonly achIndicator: boolean, public readonly policyStatus: StringUnion, public readonly managedStatus: StringUnion, public readonly managedStatusFlag: boolean) {}
}

export class ValidNewYorkCaseDetails {
  public static create(policyNumber: PolicyNumber, productType: PolicyTypes, achIndicator: boolean, policyStatus: StringUnion, managedStatus: StringUnion, managedStatusFlag: boolean) {
    return isLoadedPolicyTypes(productType) &&
      productType.selectedPolicyTypes.length === 1 &&
      (isLoaded(policyNumber) || isReserved(policyNumber))
      ? new ValidNewYorkCaseDetails(policyNumber, productType, achIndicator, policyStatus, managedStatus, managedStatusFlag)
      : new InvalidNewYorkCaseDetails(policyNumber, productType, achIndicator, policyStatus, managedStatus, managedStatusFlag);
  }

  public readonly key = 'ValidNewYorkCaseDetails';
  private constructor(
    public readonly policyNumber: Loaded<string> | Reserved,
    public readonly productType: PolicyTypes,
    public readonly achIndicator: boolean,
    public readonly policyStatus: StringUnion,
    public readonly managedStatus: StringUnion,
    public readonly managedStatusFlag: boolean,
  ) {}
}

export type NewYorkCaseDetails = ValidNewYorkCaseDetails | InvalidNewYorkCaseDetails;

export type NewYorkCaseDetailsOptions<TReturn> = ToPartialMatchOptions<NewYorkCaseDetails, 'key', TReturn>;

export const matchNewYorkCaseDetails = <TReturn>(
  caseDetails: NewYorkCaseDetails,
  options: NewYorkCaseDetailsOptions<TReturn>
): TReturn => {
  switch (caseDetails.key) {
    case 'InvalidNewYorkCaseDetails':
      return options.InvalidNewYorkCaseDetails
        ? options.InvalidNewYorkCaseDetails(caseDetails)
        : options.default(caseDetails);
    case 'ValidNewYorkCaseDetails':
      return options.ValidNewYorkCaseDetails
        ? options.ValidNewYorkCaseDetails(caseDetails)
        : options.default(caseDetails);
  }
};

export const changePolicyNumber = (policyNumber: Async<string>) => ({productType, achIndicator, policyStatus, managedStatus, managedStatusFlag}: NewYorkCaseDetails) =>
  ValidNewYorkCaseDetails.create(policyNumber, productType, achIndicator, policyStatus, managedStatus, managedStatusFlag);
export const changeUserPolicyNumber = (policyNumber: string) => ({productType, achIndicator, policyStatus, managedStatus, managedStatusFlag}: NewYorkCaseDetails) =>
  ValidNewYorkCaseDetails.create(UserAssignedPolicyNumber.create(policyNumber), productType, achIndicator, policyStatus, managedStatus, managedStatusFlag);
export const changeReservedPolicyNumber = (policyNumber: string) => ({productType, achIndicator, policyStatus, managedStatus, managedStatusFlag}: NewYorkCaseDetails) =>
  ValidNewYorkCaseDetails.create(Reserved.create(policyNumber), productType, achIndicator, policyStatus, managedStatus, managedStatusFlag);
export const changeProductType = (productType: PolicyTypes) => ({policyNumber, achIndicator, policyStatus, managedStatus, managedStatusFlag}: NewYorkCaseDetails) =>
  ValidNewYorkCaseDetails.create(policyNumber, productType, achIndicator, policyStatus, managedStatus, managedStatusFlag);
export const changePolicyNumberError = (policyNumber: string) => ({productType, achIndicator, policyStatus, managedStatus, managedStatusFlag}: NewYorkCaseDetails) =>
  ValidNewYorkCaseDetails.create(PolicyNumberError.create(policyNumber), productType, achIndicator, policyStatus, managedStatus, managedStatusFlag);
export const changePolicyNumberValidation = (policyNumber: string) => ({productType, achIndicator, policyStatus, managedStatus, managedStatusFlag}: NewYorkCaseDetails) =>
  ValidNewYorkCaseDetails.create(PolicyNumberValidating.create(policyNumber), productType, achIndicator, policyStatus, managedStatus, managedStatusFlag);

export const EmptyNewYorkCaseDetails = new InvalidNewYorkCaseDetails(
  new UserAssignedPolicyNumber(''),
  EmptyPolicyTypes,
  false,
  new EmptyString(),
  new EmptyString(),
  false
);
